import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import { highlevelConversationsService } from "../../services/highlevelConversationsService";

const tooltipStyle = { background: "#fff", border: "1px solid hsl(214 20% 88%)", borderRadius: 12, fontSize: 12, boxShadow: "0 10px 30px rgba(2,6,23,.1)" };
const PIE_COLORS = ["hsl(215 84% 22%)", "hsl(188 62% 33%)", "hsl(199 78% 38%)", "hsl(38 92% 55%)", "hsl(16 84% 56%)", "hsl(215 30% 55%)", "hsl(188 40% 60%)"];

export default function Reports() {
  const { config } = useOutletContext();
  const [sla, setSla] = useState(null);

  useEffect(() => {
    highlevelConversationsService.slaSnapshot().then(setSla);
  }, []);

  const stageBreakdown = config.stages.map((s) => ({
    stage: s.name,
    count: config.leads.filter((l) => l.stage === s.id).length,
  }));

  return (
    <div className="space-y-4 animate-fade-up">
      {/* Row 1: stage breakdown + source mix */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)] lg:col-span-2">
          <CardContent className="p-5">
            <p className="font-semibold text-sm text-foreground mb-3">Conversion by stage</p>
            <div className="h-64" data-testid="reports-stage-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stageBreakdown} margin={{ top: 6, right: 8, left: -18, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 92%)" vertical={false} />
                  <XAxis dataKey="stage" tick={{ fontSize: 9 }} interval={0} angle={-35} textAnchor="end" axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="hsl(215 84% 22%)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
          <CardContent className="p-5">
            <p className="font-semibold text-sm text-foreground mb-3">Source mix</p>
            <div className="h-52" data-testid="reports-source-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={config.sourcePerformance} dataKey="enquiries" nameKey="source" innerRadius={48} outerRadius={78} paddingAngle={2} strokeWidth={0}>
                    {config.sourcePerformance.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
              {config.sourcePerformance.map((s, i) => (
                <span key={s.source} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                  <span className="h-2 w-2 rounded-full inline-block" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /> {s.source}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: branch performance + SLA */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)] lg:col-span-2">
          <CardContent className="p-5">
            <p className="font-semibold text-sm text-foreground mb-3">{config.branchLabel} performance</p>
            <div className="h-56" data-testid="reports-branch-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={config.branchPerformance} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 92%)" vertical={false} />
                  <XAxis dataKey="branch" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="enquiries" name="Enquiries" radius={[6, 6, 0, 0]} fill="hsl(215 84% 22%)" />
                  <Bar dataKey="trials" name="Bookings" radius={[6, 6, 0, 0]} fill="hsl(188 62% 33%)" />
                  <Bar dataKey="enrolments" name="Converted" radius={[6, 6, 0, 0]} fill="hsl(38 92% 55%)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-sm text-foreground">First response SLA</p>
              {sla && <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100 text-[10px]">{sla.sla_met_pct}% within target</Badge>}
            </div>
            {sla && (
              <>
                <p className="text-2xl font-heading font-semibold tabular-nums">{sla.avg_first_response_mins} min <span className="text-xs font-normal text-muted-foreground">avg · target {sla.target_mins} min</span></p>
                <div className="h-40 mt-3" data-testid="reports-sla-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sla.buckets} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="bucket" width={64} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]} fill="hsl(188 62% 33%)" barSize={14} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: staff performance + source table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
          <CardContent className="p-5">
            <p className="font-semibold text-sm text-foreground mb-3">Staff performance</p>
            <div className="overflow-x-auto">
              <Table data-testid="reports-staff-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Staff</TableHead>
                    <TableHead className="text-xs text-right">Leads</TableHead>
                    <TableHead className="text-xs text-right">Bookings</TableHead>
                    <TableHead className="text-xs text-right">Converted</TableHead>
                    <TableHead className="text-xs text-right">Avg resp.</TableHead>
                    <TableHead className="text-xs text-right">SLA met</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {config.staffPerformance.map((s) => (
                    <TableRow key={s.staff} className="hover:bg-secondary">
                      <TableCell className="text-xs font-medium">{s.staff}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{s.leads}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{s.trials}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{s.enrolments}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{s.avg_response}m</TableCell>
                      <TableCell className="text-xs text-right">
                        <Badge variant="secondary" className={`text-[10px] tabular-nums ${s.sla_met >= 85 ? "bg-teal-100 text-teal-800" : "bg-amber-100 text-amber-800"}`}>{s.sla_met}%</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
          <CardContent className="p-5">
            <p className="font-semibold text-sm text-foreground mb-3">Source performance</p>
            <div className="overflow-x-auto">
              <Table data-testid="reports-source-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Source</TableHead>
                    <TableHead className="text-xs text-right">Enquiries</TableHead>
                    <TableHead className="text-xs text-right">Bookings</TableHead>
                    <TableHead className="text-xs text-right">Converted</TableHead>
                    <TableHead className="text-xs text-right">Conv. rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {config.sourcePerformance.map((s) => (
                    <TableRow key={s.source} className="hover:bg-secondary">
                      <TableCell className="text-xs font-medium">{s.source}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{s.enquiries}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{s.trials}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{s.enrolments}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums font-medium">{s.enquiries ? Math.round((s.enrolments / s.enquiries) * 100) : 0}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
